<template>
	<div
		class="rich-table-auto-filter-console"
		:style="positionStyle"
		@click.stop
		ref="autoFilterMenu"
	>
		<!--Sorting Section-->
		<div
			v-if="header.sortable || subHeaderSortableList.length > 0"
			class="auto-filter-section"
		>
			<div
				v-if="subHeaderSortableList.length > 0"
				class="auto-filter-row"
			>
				<span>Sort By:</span>
				<InputDropdown
					v-model:saturn="currentSubHeaderSort"
					:list="subHeaderSortableList"
					:valueMap="s => s.name"
					:text="s => s.display"
				>
				</InputDropdown>
			</div>

			<div
				:class="{
					'auto-filter-row': true,
					disabled: header.subHeaders && !currentSubHeaderSort,
				}"
			>
				<span v-if="header.sortable">Sort:</span>
				<InputRadio
					@click="autoFilterSort(true)"
					label="A➡Z"
					:modelValue="sortedAscended"
					:valueMatch="true"
				></InputRadio>
				<InputRadio
					@click="autoFilterSort(false)"
					label="Z➡A"
					:modelValue="sortedDescended"
					:valueMatch="true"
				></InputRadio>
			</div>
		</div>

		<!--Select/Clear All Filters-->
		<div
			v-if="hasAutoFilter"
			class="auto-filter-section auto-filter-select-all"
		>
			<button
				class="button-link"
				@click="toggleSelectAll(header, true)"
			>
				Select All
			</button>
			<button
				class="button-link"
				@click="toggleSelectAll(header, false)"
			>
				Clear
			</button>
		</div>

		<!--Subheader Accordion-->
		<div
			v-if="header.subHeaders && (hasAutoFilter || hasRangeFilter)"
			class="auto-filter-section multiple-sub-headers"
			ref="autoFilterAccordions"
		>
			<AccordionList
				:list="subHeaderAutoFilterList"
				listLabel="display"
				:closeOtherOnOpen="true"
			>
				<template #listHeader="{ headerItem }">
					<span
						v-if="headerItem.hasActiveRangeFilter()"
						class="fade-in-fast auto-filter-count"
					>
						<i
							v-if="headerItem.rangeFilter.min && !headerItem.rangeFilter.max"
							class="fas fa-long-arrow-alt-right"
						/>
						<i
							v-else-if="headerItem.rangeFilter.max && !headerItem.rangeFilter.min"
							class="fas fa-long-arrow-alt-left"
						/>
						<i
							v-else
							class="fas fa-exchange-alt"
						/>
					</span>
					<span
						v-if="headerItem.getNumSelectedAutoFilters() > 0"
						class="auto-filter-count fade-in-fast"
					>
						{{ headerItem.getNumSelectedAutoFilters() }}
					</span>
				</template>

				<template #listBody="{ bodyItem }">
					<div
						v-if="bodyItem.rangeFilter"
						class="range-filter-section"
						style="padding: 0 10px"
					>
						<h3
							class="filter-header"
							style="padding: 10px 0"
						>
							Range Filter
						</h3>
						<div
							v-if="bodyItem.rangeFilter.rangeFilterType == ENUMS.RICHTABLE_RANGE_FILTER_TYPES.DATE"
							:style="$grid('1-1')"
						>
							<InputDate
								label="From"
								v-model:saturn="bodyItem.rangeFilter.min"
								:showIcon="false"
								@change="rangeFilterChange(bodyItem)"
							/>
							<InputDate
								label="To"
								v-model:saturn="bodyItem.rangeFilter.max"
								:showIcon="false"
								@change="rangeFilterChange(bodyItem)"
							/>
						</div>

						<div
							v-else-if="bodyItem.rangeFilter.rangeFilterType == ENUMS.RICHTABLE_RANGE_FILTER_TYPES.TERM"
							:style="$grid('1-1')"
						>
							<InputTextbox
								label="After"
								v-model:saturn="bodyItem.rangeFilter.min"
								:showIcon="false"
								type="text"
								@change="rangeFilterChange(bodyItem)"
							/>
							<InputTextbox
								label="Before"
								v-model:saturn="bodyItem.rangeFilter.max"
								:showIcon="false"
								type="text"
								@change="rangeFilterChange(bodyItem)"
							/>
						</div>

						<div
							v-else
							:style="$grid('1-1')"
						>
							<InputTextbox
								label="Min"
								v-model:saturn="bodyItem.rangeFilter.min"
								:showIcon="false"
								type="number"
								@change="rangeFilterChange(bodyItem)"
							/>
							<InputTextbox
								label="Max"
								v-model:saturn="bodyItem.rangeFilter.max"
								:showIcon="false"
								type="number"
								@change="rangeFilterChange(bodyItem)"
							/>
						</div>
					</div>

					<div
						v-if="bodyItem.autoFilter"
						class="auto-filter-section"
					>
						<h3 class="filter-header">Auto-Filter</h3>
						<div
							v-show="!bodyItem.autoFilter.hideSearch"
							class="auto-filter-search-values"
							@click.stop
						>
							<div class="search-icon"><i class="fas fa-search"></i></div>
							<input v-model="bodyItem.autoFilter.searchText" />
						</div>

						<div
							:class="{
								'auto-filter-values': true,
								'flex-view': bodyItem.hasFlexDisplay(),
								'grid-view': bodyItem.hasGridDisplay(),
							}"
							:style="bodyItem.getGridColumnCssVar()"
						>
							<div
								v-for="v in getAutoFilterValues(bodyItem)"
								:key="v.$index"
								class="auto-filter-option"
								@click.stop="toggleSubHeaderFilter(bodyItem, v)"
							>
								<span
									class="autofilter-checkbox"
									:class="{ 'unchecked-checkbox': !v.isSelected }"
									><i class="fas fa-check"></i
								></span>
								<span class="autofilter-display">{{ v.display }}</span>
							</div>
						</div>
					</div>
				</template>
			</AccordionList>
		</div>

		<!--Range Filter-->
		<div
			v-else-if="hasRangeFilter"
			class="range-filter-section"
		>
			<h3
				v-if="hasAutoFilter"
				class="filter-header"
			>
				Range Filter
			</h3>
			<div
				v-if="header.rangeFilter.rangeFilterType == ENUMS.RICHTABLE_RANGE_FILTER_TYPES.DATE"
				:style="$grid('1-1')"
			>
				<InputDate
					label="From"
					v-model:saturn="header.rangeFilter.min"
					:showIcon="false"
					@change="rangeFilterChange(header)"
				/>
				<InputDate
					label="To"
					v-model:saturn="header.rangeFilter.max"
					:showIcon="false"
					@change="rangeFilterChange(header)"
				/>
			</div>

			<div
				v-else-if="header.rangeFilter.rangeFilterType == ENUMS.RICHTABLE_RANGE_FILTER_TYPES.TERM"
				:style="$grid('1-1')"
			>
				<InputTextbox
					label="After"
					v-model:saturn="header.rangeFilter.min"
					:showIcon="false"
					type="text"
					@change="rangeFilterChange(header)"
				/>
				<InputTextbox
					label="Before"
					v-model:saturn="header.rangeFilter.max"
					:showIcon="false"
					type="text"
					@change="rangeFilterChange(header)"
				/>
			</div>

			<div
				v-else
				:style="$grid('1-1')"
			>
				<InputTextbox
					label="Min"
					v-model:saturn="header.rangeFilter.min"
					:showIcon="false"
					type="number"
					@change="rangeFilterChange(header)"
				/>
				<InputTextbox
					label="Max"
					v-model:saturn="header.rangeFilter.max"
					:showIcon="false"
					type="number"
					@change="rangeFilterChange(header)"
				/>
			</div>
		</div>

		<!--Auto Filter-->
		<div
			v-else-if="hasAutoFilter"
			class="auto-filter-section"
		>
			<h3
				v-if="hasRangeFilter"
				class="filter-header"
			>
				Auto Filter
			</h3>
			<div
				v-show="!header.autoFilter.hideSearch"
				class="auto-filter-search-values"
				@click.stop
			>
				<div class="search-icon"><i class="fas fa-search"></i></div>
				<input v-model="header.autoFilter.searchText" />
			</div>

			<div
				:class="{
					'auto-filter-values': true,
					'flex-view': header.hasFlexDisplay(),
					'grid-view': header.hasGridDisplay(),
				}"
				:style="header.getGridColumnCssVar()"
				ref="autoFilterValues"
			>
				<div
					v-for="v in getAutoFilterValues(header)"
					:key="v.$index"
					class="auto-filter-option"
					@click.stop="toggleAutoFilterValue(header, v)"
				>
					<span
						class="autofilter-checkbox"
						:class="{ 'unchecked-checkbox': !v.isSelected }"
						><i class="fas fa-check"
					/></span>
					<span class="autofilter-display">{{ v.display }}</span>
				</div>
			</div>
		</div>

		<!--Close Button-->
		<div class="auto-filter-section auto-filter-close">
			<button
				class="button-block"
				@click.stop="onHeaderClicked(header)"
			>
				Close
			</button>
		</div>
	</div>
</template>

<script>
	import util from '@core/services/util';

	import ENUMS from '@core/classes/Enums';
	import RichTableHeader from '@core/classes/RichTableHeader';

	import AccordionList from '@core/components/AccordionList.vue';
	import InputDate from '@core/components/InputDate.vue';
	import InputDropdown from '@core/components/InputDropdown.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';

	export default {
		name: 'RichTableAutoFilter',
		props: {
			headerRef: [Object, HTMLTableCellElement],
			header: RichTableHeader,
			sort: Function,
			sortBy: String,
			asc: Boolean,
			autoFilterToggleSelectAll: Function,
			toggleAutoFilterValue: Function,
			onRangeFilterChanged: Function,
			onHeaderClicked: Function,
		},
		data() {
			return {
				currentSubHeaderSort: null,
				positionStyle: null,
			};
		},
		computed: {
			ENUMS() {
				return ENUMS;
			},
			sortedAscended() {
				const sortName = this.header.subHeaders ? this.currentSubHeaderSort : this.header.name;
				return this.sortBy === sortName && this.asc;
			},
			sortedDescended() {
				const sortName = this.header.subHeaders ? this.currentSubHeaderSort : this.header.name;
				return this.sortBy === sortName && !this.asc;
			},
			subHeaderSortableList() {
				if (!this.header.subHeaders) return [];
				return this.header.subHeaders.filter(sh => sh.sortable);
			},
			subHeaderAutoFilterList() {
				if (!this.header.subHeaders) return [];
				return this.header.subHeaders.filter(sh => sh.autoFilter || sh.rangeFilter);
			},
			hasAutoFilter() {
				return this.header.autoFilter || this.header.subHeaders?.some(sh => sh.autoFilter);
			},
			hasRangeFilter() {
				return this.header.rangeFilter || this.header.subHeaders?.some(sh => sh.rangeFilter);
			},
		},
		created() {
			if (this.subHeaderSortableList.some(sh => sh.name === this.sortBy)) this.currentSubHeaderSort = this.sortBy;
		},
		mounted() {
			this.setPositionMenu();
			window.addEventListener('resize', this.setPositionMenu);
			window.addEventListener('scroll', this.setPositionMenu, true);
		},
		beforeUnmount() {
			window.removeEventListener('resize', this.setPositionMenu);
			window.removeEventListener('scroll', this.setPositionMenu, true);
		},
		methods: {
			setPositionMenu() {
				this.positionStyle = this.positionMenu();
			},
			positionMenu() {
				const headerPosition = this.headerRef?.getBoundingClientRect();
				if (!headerPosition) return null;

				const output = { position: 'fixed' };

				const footerHeight = Number.parseInt(getComputedStyle(document.body).getPropertyValue('--footer-height') ?? 25);

				const autoFilterRef = this.$refs.autoFilterMenu;
				const autoFilterRect = autoFilterRef?.getBoundingClientRect();

				// Calculating whether the menu will render off screen
				const menuPosLeft = headerPosition.left + (autoFilterRect?.width ?? 0);
				const menuPosBottom = headerPosition.bottom + (autoFilterRect?.height ?? 0);
				const menuPosTop = headerPosition.top - (autoFilterRect?.height ?? 0);

				const renderLeft = window.innerWidth - menuPosLeft < 0;

				// Will render top if there's enough space above the header
				const renderTop = window.innerHeight - menuPosBottom < footerHeight + 5 && menuPosTop > 0;

				output.left = `${renderLeft ? headerPosition.right : headerPosition.left}px`;
				output.top = `${renderTop ? headerPosition.top : headerPosition.top + headerPosition.height}px`;
				output.transform = `translate(${renderLeft ? '-100%' : '0%'}, ${renderTop ? '-100%' : '0%'})`;

				// If can't render top, will try to shrink the menu instead to fit.
				const filterValuesRef = this.$refs.autoFilterAccordions ?? this.$refs.autoFilterValues;
				if (!renderTop && autoFilterRect && filterValuesRef) {
					const filterValuesRect = filterValuesRef?.getBoundingClientRect();

					const heightDiff = window.innerHeight - autoFilterRect.bottom - (footerHeight + 5);
					const newHeight = Math.max(Math.min(300, filterValuesRect.height + heightDiff), 100);

					filterValuesRef.style.maxHeight = `${Math.min(newHeight, 300)}px`;
				}

				// Adding class names to indicate how the menu got positioned
				if (autoFilterRef) {
					if (renderTop) autoFilterRef.classList.add('top');
					else autoFilterRef.classList.remove('top');

					if (renderLeft) autoFilterRef.classList.add('left');
					else autoFilterRef.classList.remove('left');
				}

				return output;
			},
			getAutoFilterValues(header) {
				if (!header.autoFilter || !header.autoFilter.values) return [];

				return header.autoFilter.values.filter(v => {
					return header.autoFilter.searchText ? v.display.toLowerCase().search(header.autoFilter.searchText.toLowerCase()) > -1 : true;
				});
			},
			autoFilterSort(asc) {
				if (!this.header.subHeaders) {
					this.sort(this.header, asc);
					return;
				}

				const selectedHeader = this.header.subHeaders.find(sh => sh.name === this.currentSubHeaderSort);
				this.sort(selectedHeader, asc);
			},
			toggleSelectAll(header, toggle) {
				if (header.autoFilter && !header.subHeaders) {
					this.autoFilterToggleSelectAll(header, toggle);
					return;
				}

				header.anythingChanged = true;
				header.subHeaders.filter(h => h.autoFilter).forEach(sh => this.autoFilterToggleSelectAll(sh, toggle));
			},
			toggleSubHeaderFilter(subHeader, value) {
				this.toggleAutoFilterValue(subHeader, value);
			},
			rangeFilterChange(header) {
				util.debounce(this.onRangeFilterChanged, 100, header, header.rangeFilter.min, header.rangeFilter.max);
			},
		},
		components: {
			InputRadio,
			InputDropdown,
			AccordionList,
			InputTextbox,
			InputDate,
		},
	};
</script>
<style>
	.auto-filter-select-all > * {
		font-weight: bold;
	}

	.auto-filter-values.flex-view {
		display: flex;
		flex-wrap: wrap;
	}

	.auto-filter-values.flex-view > * {
		flex: 1;
	}

	.auto-filter-values.grid-view {
		--grid-columns: 1;

		display: grid;
		grid-template-columns: repeat(var(--grid-columns), 1fr);
	}

	.range-filter-section > div > fieldset.input-component-wrapper {
		position: unset;
	}

	.multiple-sub-headers .auto-filter-count > i {
		position: relative;
	}
</style>
